import { useRef, useState } from 'react'
import { AlertTriangle, FileUp, Loader2 } from 'lucide-react'
import { useNotification } from '../../../contexts/NotificationContext'
import { parseContactCsv } from '../../../lib/csv'
import { importContacts } from '../../../services/crmService'
import { Button } from '../../ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'

// Consent has to be stated per import, because only the person doing the
// importing knows where the list came from. Defaulting to "opted in" would be
// the CRM quietly manufacturing permission it has no evidence for.
const CONSENT_OPTIONS = [
  {
    value: 'opted_in',
    label: 'They opted in',
    hint: 'A newsletter list, or anyone who explicitly asked for updates. Mailable by default.',
  },
  {
    value: 'none',
    label: 'No recorded consent',
    hint: 'A customer or contact list with no opt-in behind it. Imported, but held out of every audience.',
  },
]

export default function ImportDialog({ open, onOpenChange, onImported }) {
  const notify = useNotification()
  const fileInput = useRef(null)
  const [parsed, setParsed] = useState(null)
  const [fileName, setFileName] = useState('')
  const [consent, setConsent] = useState('opted_in')
  const [consentSource, setConsentSource] = useState('')
  const [importing, setImporting] = useState(false)

  const reset = () => {
    setParsed(null)
    setFileName('')
    setConsentSource('')
    setConsent('opted_in')
    if (fileInput.current) fileInput.current.value = ''
  }

  const close = (next) => {
    if (!next) reset()
    onOpenChange(next)
  }

  const onFile = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    try {
      const result = parseContactCsv(await file.text())
      setParsed(result)
      if (result.error) notify.error(result.error)
    } catch (err) {
      notify.error(err.message || 'Could not read that file')
      setParsed(null)
    }
  }

  const runImport = async () => {
    if (!parsed?.rows.length) return
    setImporting(true)
    try {
      const result = await importContacts(parsed.rows, {
        source: 'import',
        consent,
        consentSource: consentSource.trim() || `CSV import — ${fileName || 'unnamed file'}`,
      })
      if (result.failed) {
        notify.warning(`Imported ${result.imported}; ${result.failed} could not be saved`)
        for (const message of result.errors.slice(0, 3)) notify.error(message)
      } else {
        notify.success(`Imported ${result.imported} contact${result.imported === 1 ? '' : 's'}`)
      }
      onImported?.()
      close(false)
    } catch (err) {
      notify.error(err.message || 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import contacts from CSV</DialogTitle>
          <DialogDescription>
            Needs an <code>email</code> column; <code>first name</code> and{' '}
            <code>last name</code> are used when present. A file of bare
            addresses with no header works too.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              className="hidden"
              id="crm-csv-input"
            />
            <Button variant="outline" className="w-full" onClick={() => fileInput.current?.click()}>
              <FileUp className="mr-2 h-4 w-4" />
              {fileName || 'Choose a CSV file'}
            </Button>
          </div>

          {parsed && !parsed.error && (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
              <strong>{parsed.rows.length.toLocaleString()}</strong> contact
              {parsed.rows.length === 1 ? '' : 's'} ready to import
              {parsed.duplicates > 0 && ` · ${parsed.duplicates} duplicate${parsed.duplicates === 1 ? '' : 's'} merged`}
              {parsed.skipped.length > 0 && ` · ${parsed.skipped.length} skipped as invalid`}
            </div>
          )}

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Where did this list come from?</legend>
            {CONSENT_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer gap-3 rounded-md border border-border p-3 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              >
                <input
                  type="radio"
                  name="crm-import-consent"
                  value={option.value}
                  checked={consent === option.value}
                  onChange={() => setConsent(option.value)}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">{option.label}</span>
                  <span className="block text-xs text-muted-foreground">{option.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="space-y-1.5">
            <Label htmlFor="crm-consent-source">What is this list? (recorded against every contact)</Label>
            <Input
              id="crm-consent-source"
              value={consentSource}
              onChange={(e) => setConsentSource(e.target.value)}
              placeholder="e.g. Zoho newsletter export, Aug 2026"
            />
          </div>

          {consent === 'opted_in' && (
            <p className="flex gap-2 rounded-md border border-[var(--orange-6)] bg-[var(--orange-3)] px-3 py-2 text-xs text-[var(--orange-11)]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              Only mark a list as opted in if these people actually asked for
              email from dub. Mailing a bought or scraped list is what gets a
              sending domain blocked.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={importing}>Cancel</Button>
          <Button onClick={runImport} disabled={importing || !parsed?.rows.length}>
            {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Import {parsed?.rows.length ? parsed.rows.length.toLocaleString() : ''} contacts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
